import { DateTime } from 'luxon';
import { normalizeWeatherForecastDayDate } from '../day/dayQuery';
import { formatWeatherForecastDayLabel } from '../day/formatDayLabel';
import { FREE_FORECAST_DAYS } from '../forecastHorizon';

export interface WeatherMoonPhaseApiDay {
  date?: string;
  riseDateTime?: string | null;
  setDateTime?: string | null;
  label?: string;
  code?: string;
  percentageVisible?: number;
  hemisphere?: 'northern' | 'southern' | null;
}

export interface WeatherMoonPhasesApiResponse {
  weatherLocation?: {
    timeZone?: string;
  };
  moonPhases?: WeatherMoonPhaseApiDay[];
}

export interface WeatherForecastMoonPhaseDay {
  date: string;
  dayLabel: string;
  label: string;
  code: string;
  percentageVisible: number;
  hemisphere: 'northern' | 'southern' | null;
  riseTime: string | null;
  setTime: string | null;
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const formatMoonEventTime = (
  dateTime: string | null | undefined,
  timeZone: string,
): string | null => {
  if (!dateTime) {
    return null;
  }

  const parsed = DateTime.fromISO(dateTime, { zone: timeZone });
  return parsed.isValid ? parsed.toFormat('h:mma').toLowerCase() : null;
};

/** Match outlook day normalization — parse in location TZ, not UTC date prefix. */
const normalizeMoonPhaseDateInTimeZone = (
  rawDate: string,
  timeZone: string,
): string => {
  if (DATE_ONLY_PATTERN.test(rawDate)) {
    return rawDate;
  }

  const parsedInZone = DateTime.fromISO(rawDate, { zone: timeZone });
  if (parsedInZone.isValid) {
    return parsedInZone.toISODate() ?? rawDate;
  }

  return normalizeWeatherForecastDayDate(rawDate) ?? rawDate;
};

export const formatWeatherForecastMoonPhases = (
  data: WeatherMoonPhasesApiResponse | null | undefined,
  maxDays = FREE_FORECAST_DAYS,
): WeatherForecastMoonPhaseDay[] => {
  const timeZone = data?.weatherLocation?.timeZone;
  const moonPhases = data?.moonPhases;

  if (!timeZone || !moonPhases?.length) {
    return [];
  }

  const today = DateTime.now().setZone(timeZone).startOf('day');

  return moonPhases
    .map((moonPhaseDay) => {
      const rawDate = moonPhaseDay.date ?? '';
      const date = normalizeMoonPhaseDateInTimeZone(rawDate, timeZone);
      const parsedDate = DateTime.fromISO(date, { zone: timeZone });
      const isToday = parsedDate.isValid && parsedDate.hasSame(today, 'day');

      return {
        date,
        dayLabel: formatWeatherForecastDayLabel(parsedDate, isToday),
        label: moonPhaseDay.label ?? '',
        code: moonPhaseDay.code ?? '',
        percentageVisible: moonPhaseDay.percentageVisible ?? 0,
        hemisphere: moonPhaseDay.hemisphere ?? null,
        riseTime: formatMoonEventTime(moonPhaseDay.riseDateTime, timeZone),
        setTime: formatMoonEventTime(moonPhaseDay.setDateTime, timeZone),
        parsedDate,
      };
    })
    .filter((moonPhaseDay) => moonPhaseDay.parsedDate >= today)
    .slice(0, maxDays)
    .map(({ parsedDate: _parsedDate, ...moonPhaseDay }) => moonPhaseDay);
};
